/* eslint-disable import/no-duplicates */
import React, { Suspense } from 'react';
// @mui
import { useTheme } from '@mui/material';
import {
  CalendarIcon,
  DateRange,
  DateRangePicker,
  LocalizationProvider,
  PickersShortcutsItem,
} from '@mui/x-date-pickers-pro';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import useResponsive from 'src/hooks/useResponsive';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { useDate } from 'src/hooks/use-date';
import useDateFilter from 'src/hooks/useDateFilter';
import {
  addMonths,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';
import { getValidDate } from 'src/utils/dateTime';
import useMerchant from 'src/hooks/useMerchant';
import { StackRowForm } from '../stack-column';
import { AppSelect } from '../app-select';
import { TIMEZONE } from 'src/contexts/DateFilterContext';

//
//

const shortcutsItems: PickersShortcutsItem<DateRange<Date>>[] = [
  {
    label: 'This week',
    getValue: () => {
      const today = new Date();
      const startOfWeekDate = startOfWeek(today);
      const endOfWeekDate = endOfWeek(today);
      return [startOfWeekDate, endOfWeekDate];
    },
  },
  {
    label: 'Last week',
    getValue: () => {
      const today = new Date();
      const prevWeek = subWeeks(today, 1);
      const startOfLastWeek = startOfWeek(prevWeek);
      const endOfLastWeek = endOfWeek(prevWeek);
      return [startOfLastWeek, endOfLastWeek];
    },
  },
  {
    label: 'This month',
    getValue: () => {
      const today = new Date();
      const startOfMonthDate = startOfMonth(today);
      const endOfMonthDate = endOfMonth(today);
      return [startOfMonthDate, endOfMonthDate];
    },
  },
  {
    label: 'Last month',
    getValue: () => {
      const today = new Date();
      const prevMonth = subMonths(today, 1);
      const startOfLastMonth = startOfMonth(prevMonth);
      const endOfLastMonth = endOfMonth(prevMonth);
      return [startOfLastMonth, endOfLastMonth];
    },
  },
  {
    label: 'This quarter',
    getValue: () => {
      const today = new Date();
      const quarter = Math.floor(today.getMonth() / 3);
      const startOfQuarter = startOfMonth(addMonths(startOfYear(today), quarter * 3));
      const endOfQuarter = endOfMonth(addMonths(startOfQuarter, 2));
      return [startOfQuarter, endOfQuarter];
    },
  },
  {
    label: 'Last quarter',
    getValue: () => {
      const today = new Date();
      const currentQuarter = Math.floor(today.getMonth() / 3);
      const startOfLastQuarter = startOfMonth(
        addMonths(startOfYear(today), (currentQuarter - 1) * 3)
      );
      const endOfLastQuarter = endOfMonth(addMonths(startOfLastQuarter, 2));
      return [startOfLastQuarter, endOfLastQuarter];
    },
  },
  {
    label: 'This year',
    getValue: () => {
      const today = new Date();
      const start = startOfYear(today);
      const end = endOfYear(today);
      return [start, end];
    },
  },
  {
    label: 'Last year',
    getValue: () => {
      const today = new Date();
      const lastYear = subYears(today, 1);
      const start = startOfYear(lastYear);
      const end = endOfYear(lastYear);
      return [start, end];
    },
  },
  {
    label: 'Reset',
    getValue: () => {
      const today = new Date();
      const sixMonthsAgo = subMonths(today, 6);
      const startOfSixMonthsAgo = startOfMonth(sixMonthsAgo);
      const endOfToday = endOfDay(today);
      return [startOfSixMonthsAgo, endOfToday];
    },
  },
];

interface Props {
  dateRange?: { from: string | null; to: string | null };
  onChange?: (val: string | null, flag: string) => void;
  onUpdate?: () => void;
  children?: JSX.Element;
}

const RangeDatePicker: React.FC<Props> = ({ onUpdate, children }) => {
  const { handleChange, localDateRange, setLocalDateRange, selectedTimezone, setSelectedTimezone } = useDateFilter();
  const theme = useTheme();
  const isTablet = useResponsive('up', 'sm');
  const { locale } = useDate();
  const { isMerchantLoading } = useMerchant();

  const [open, setOpen] = React.useState(false);

  const calendarOpener = (): void => {
    setOpen((prevState) => !prevState);
  };

  if (isMerchantLoading || !locale) return null;

  return  (
    <Suspense fallback={null} >
      <StackRowForm gap={1}>
      <AppSelect
        name=""
        size="medium"
        value={selectedTimezone}
        placeholder="Select timezone"
        options={[
          { value: TIMEZONE.UTC, label: 'UTC timezone' },
          { value: TIMEZONE.LOCAL, label: 'Local timezone' },
        ]}
        fullWidth
        onChange={(ev) => setSelectedTimezone(ev?.target?.value as TIMEZONE)}
      />

    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={locale}
      localeText={{ cancelButtonLabel: 'Cancel', okButtonLabel: 'Apply' }}
    >
      <DateRangePicker
        onChange={(newValue) => {
          setLocalDateRange({ from: getValidDate(newValue[0]), to: getValidDate(newValue[1]) });
        }}
        onAccept={(newValue) => {
          handleChange(localDateRange);
        }}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        defaultValue={[localDateRange.from, localDateRange.to]}
        slotProps={{
          shortcuts: {
            items: shortcutsItems,
            changeImportance: 'set',
            sx: {
              py: 2,
              pl: 1,
              pr: 0,
              display: isTablet ? 'grid' : 'initial',
              gridTemplateColumns: '1fr 1fr',
              height: 'max-content',
              '& .MuiListItem-root': {
                p: 0,
                m: 0,
                '& .MuiButtonBase-root': {
                  background: (theme) => theme.palette.grey[200],
                  borderRadius: 0,
                  width: '100%',
                  m: 0.6,
                  p: 1.5,
                  height: 'auto',
                  fontWeight: 400,
                  ...theme.typography.body3,
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  '& .MuiChip-label': {
                    p: 0,
                  },
                },
              },
            },
          },
          actionBar: {
            actions: ['cancel', 'accept'],
            sx: {
              p: 2,
              '& .MuiButton-root': {
                fontSize: 14,
                p: (theme) => theme.spacing(1, 1.5),
              },
            },
          },
          textField: {
            fullWidth: true,
            InputProps: {
              endAdornment: <CalendarIcon onClick={calendarOpener} sx={{ '&:hover': { cursor: 'pointer' } }} />
            },
          },
        }}
        minDate={new Date('01/01/2000 00:00')}
        slots={{ field: SingleInputDateRangeField }}
        calendars={2}
        closeOnSelect={false}
        open={open}
        sx={{ minWidth: { xs: '100%', sm: 300 } }}
      />
    </LocalizationProvider>
      </StackRowForm>
    </Suspense>
  );
};

export default RangeDatePicker;
